import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Phone,
  Calendar,
  IndianRupee,
  MessageSquare,
  Clock,
  QrCode,
  CreditCard,
} from "lucide-react";

const CustomerDetailPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { API, getAuthHeader } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomer();
  }, [customerId]);

  const fetchCustomer = async () => {
    try {
      const response = await axios.get(`${API}/customers/${customerId}`, getAuthHeader());
      setCustomer(response.data);
    } catch (error) {
      console.error("Fetch customer error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-500">Loading customer details...</div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-zinc-500">Customer not found</p>
        <Button onClick={() => navigate("/customers")}>Back to Customers</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/customers")}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Customer Info Card */}
      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-6">
            <div className="w-16 h-16 bg-[#0A192F] flex items-center justify-center text-2xl font-bold text-white">
              {customer.name?.charAt(0).toUpperCase() || "?"}
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-zinc-950" data-testid="customer-name">
                  {customer.name}
                </h1>
                {customer.whatsapp_name && customer.whatsapp_name !== customer.name && (
                  <p className="text-sm text-zinc-500">WhatsApp: {customer.whatsapp_name}</p>
                )}
              </div>
              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2 text-zinc-700">
                  <Phone className="w-4 h-4 text-zinc-400" />
                  {customer.phone}
                </div>
                <div className="flex items-center gap-2 text-zinc-700">
                  <Calendar className="w-4 h-4 text-zinc-400" />
                  Joined {formatDateTime(customer.created_at)}
                </div>
                <div className="flex items-center gap-2 text-zinc-700">
                  <IndianRupee className="w-4 h-4 text-zinc-400" />
                  Total Spent: {formatCurrency(customer.total_spent)}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Badge variant="secondary" className="text-sm">
                {customer.bookings?.length || 0} Bookings
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="messages" className="space-y-4">
        <TabsList className="bg-zinc-100">
          <TabsTrigger value="messages" data-testid="tab-messages">
            <MessageSquare className="w-4 h-4 mr-2" />
            WhatsApp Chat
          </TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">
            <CreditCard className="w-4 h-4 mr-2" />
            Bookings
          </TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">
            <Clock className="w-4 h-4 mr-2" />
            Activity Log
          </TabsTrigger>
        </TabsList>

        {/* Messages Tab */}
        <TabsContent value="messages">
          <Card className="border-zinc-200 shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-zinc-700 uppercase tracking-wider">
                WhatsApp Conversation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-96">
                {customer.messages?.length > 0 ? (
                  <div className="space-y-3 pr-4">
                    {[...customer.messages].reverse().map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.direction === "outbound" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[75%] px-4 py-2 ${
                            msg.direction === "outbound"
                              ? "chat-bubble-outbound"
                              : "chat-bubble-inbound"
                          }`}
                        >
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-xs mt-1 ${msg.direction === "outbound" ? "text-white/60" : "text-zinc-400"}`}>
                            {formatTime(msg.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-48 text-zinc-400">
                    <MessageSquare className="w-8 h-8 mb-2" />
                    <p>No messages yet</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bookings Tab */}
        <TabsContent value="bookings">
          <Card className="border-zinc-200 shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-zinc-700 uppercase tracking-wider">
                Booking History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {customer.bookings?.length > 0 ? (
                <div className="space-y-4">
                  {customer.bookings.map((booking) => (
                    <div
                      key={booking.id}
                      className="p-4 border border-zinc-200 space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-zinc-900">{booking.booking_number || booking.id}</span>
                        <Badge
                          className={`text-xs ${
                            booking.status === "confirmed"
                              ? "bg-emerald-100 text-emerald-700"
                              : booking.status === "completed"
                              ? "bg-blue-100 text-blue-700"
                              : booking.status === "cancelled" || booking.status === "cancel_requested"
                              ? "bg-red-100 text-red-700"
                              : booking.status === "modify_requested"
                              ? "bg-orange-100 text-orange-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {booking.status?.replace(/_/g, " ")}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-zinc-500">Date</p>
                          <p className="font-medium">{booking.service_date || booking.travel_date || "-"}</p>
                        </div>
                        <div>
                          <p className="text-zinc-500">Type</p>
                          <p className="font-medium capitalize">{booking.travel_type || "-"}</p>
                        </div>
                        <div>
                          <p className="text-zinc-500">Direction</p>
                          <p className="font-medium capitalize">{booking.travel_direction || booking.zone || "-"}</p>
                        </div>
                        <div>
                          <p className="text-zinc-500">Amount</p>
                          <p className="font-medium">{formatCurrency(booking.total_amount)}</p>
                        </div>
                      </div>
                      {booking.service_name ? (
                        <div className="flex flex-wrap gap-2 pt-2">
                          <Badge variant="secondary" className="text-xs">
                            {booking.service_name}
                          </Badge>
                        </div>
                      ) : booking.services?.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-2">
                          {booking.services.map((s, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {s.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {(booking.flight_number || booking.pnr) && (
                        <div className="flex gap-4 text-xs text-zinc-500 pt-1">
                          {booking.flight_number && <span>Flight: {booking.flight_number}</span>}
                          {booking.pnr && <span>PNR: {booking.pnr}</span>}
                          {booking.pax_count && <span>Pax: {booking.pax_count}</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-zinc-400">
                  <CreditCard className="w-8 h-8 mb-2" />
                  <p>No bookings yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity">
          <Card className="border-zinc-200 shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-zinc-700 uppercase tracking-wider">
                Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-96">
                {customer.activity_log?.length > 0 ? (
                  <div className="space-y-4 pr-4">
                    {[...customer.activity_log].reverse().map((activity, idx) => (
                      <div key={idx} className="flex gap-4 pb-4 border-b border-zinc-100 last:border-0">
                        <div
                          className={`w-8 h-8 flex items-center justify-center ${
                            activity.type === "qr_scan"
                              ? "bg-violet-100"
                              : activity.type === "booking_created"
                              ? "bg-blue-100"
                              : activity.type === "payment_completed"
                              ? "bg-emerald-100"
                              : "bg-zinc-100"
                          }`}
                        >
                          {activity.type === "qr_scan" ? (
                            <QrCode className="w-4 h-4 text-violet-600" />
                          ) : activity.type === "booking_created" ? (
                            <CreditCard className="w-4 h-4 text-blue-600" />
                          ) : activity.type === "payment_completed" ? (
                            <IndianRupee className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <MessageSquare className="w-4 h-4 text-zinc-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-zinc-900 capitalize">
                            {activity.type?.replace(/_/g, " ")}
                          </p>
                          {activity.location && (
                            <p className="text-xs text-zinc-500">Location: {activity.location}</p>
                          )}
                          {activity.amount && (
                            <p className="text-xs text-zinc-500">Amount: {formatCurrency(activity.amount)}</p>
                          )}
                          {activity.content && (
                            <p className="text-xs text-zinc-500 truncate max-w-xs">{activity.content}</p>
                          )}
                          <p className="text-xs text-zinc-400 mt-1">{formatDateTime(activity.timestamp)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-48 text-zinc-400">
                    <Clock className="w-8 h-8 mb-2" />
                    <p>No activity recorded</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CustomerDetailPage;
